<script setup lang="ts">
import type { Option } from '@/modules/common/types/option.type.ts';
import { uniqueId } from 'lodash-es';
import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';
import UiSpoiler from '@/modules/ui/components/ui/UiSpoiler.vue';

export type OptionColor = 'yellow' | 'green' | 'red' | 'gray';

export type RadioOption<T extends string | number = string | number> = Option<T> & {
  color?: OptionColor;
};

const model = defineModel<number | string>({ required: true });

const { id = uniqueId(IdPrefix.Control), options } = defineProps<{
  id?: string;
  disabled?: boolean;
  readonly?: boolean;
  options: RadioOption[];
}>();
</script>

<template>
  <div class="ui-radio">
    <div
      v-for="(option, index) in options"
      :key="option.code"
    >
      <input
        :id="`${id}-${option.code}`"
        v-model="model"
        class="input"
        type="radio"
        :name="id"
        :value="option.code"
        :disabled="disabled || option.disabled"
        :readonly="readonly"
      />

      <label
        class="label"
        :class="`_color-${option.color || 'yellow'}`"
        :for="`${id}-${option.code}`"
      >
        {{ $maybeT(option.label) }}
      </label>

      <UiSpoiler
        v-if="$slots[index]"
        :open="model === option.code"
      >
        <slot :name="index" />
      </UiSpoiler>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ui-radio {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: var(--font-size-l);
  line-height: var(--line-height-l);
}

.input {
  display: none;

  &:checked {
    & + .label {
      &:after {
        opacity: 1;
      }

      &._color-yellow {
        background-color: var(--color-yellow-500);
      }

      &._color-green {
        background-color: var(--color-green-radio);
      }

      &._color-red {
        background-color: var(--color-red-300);
      }

      &._color-gray {
        background-color: var(--color-gray-100);
      }
    }
  }

  &:disabled {
    & + .label {
      opacity: 0.5;
    }
  }

  &:not(:disabled) {
    & + .label {
      cursor: pointer;
    }
  }

  @media (hover: hover) {
    &:not(:disabled, :checked) {
      & + .label {
        &:hover {
          opacity: 0.7;
        }
      }
    }
  }
}

.label {
  position: relative;
  z-index: 1;
  display: block;
  padding: 10px 16px 10px 40px;
  color: var(--color-gray-800);
  background-color: var(--color-gray-075);
  border-radius: 8px;
  transition:
    background-color var(--transition-fast),
    opacity var(--transition-fast);

  &:before {
    content: '';
    position: absolute;
    top: 50%;
    left: 24px;
    display: block;
    transform: translate(-50%, -50%);
    width: 10px;
    height: 10px;
    border-radius: 50%;
    box-shadow: var(--shadow-radio-button);
  }

  &:after {
    content: '';
    position: absolute;
    top: 50%;
    left: 24px;
    display: block;
    transform: translate(-50%, -50%);
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: var(--color-gray-800);
    opacity: 0;
    transition: opacity var(--transition-fast);
  }
}

.ui-spoiler {
  position: relative;
  z-index: 2;
  border-radius: 0 0 8px 8px;
  border: var(--border-dashed-gray-400);
  border-top: none;
  transform: translateY(-8px);

  > :deep(.content) {
    padding: 24px 16px 16px;
  }
}
</style>
